// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import React, { useMemo } from "react";
import { NavLink } from "react-router";
import { useSelector, shallowEqual } from "react-redux";

import { useMediaQuery } from "../../../../hooks/useMediaQuery";

import { Icons } from "../../../../../web-utils/src/UI/Component/React/V1/Icons/Icons";

import { UserInvitations } from "../UserInvitations/UserInvitations";
import { UserNavigation } from "../UserNavigation/UserNavigation";
import { StudioNavigation } from "../StudioNavigation/StudioNavigation";
import { CompanyNavigation } from "../CompanyNavigation/CompanyNavigation";

import styles from "../Navigation.module.css";

import button from "../../../UI/Button/Button.module.css";

const getNavigationLinks = ({ isLocalUser }) => {
  const isCloudUser = !isLocalUser;

  return {
    mainLinks: [
      {
        id: "dashboard",
        to: "/",
        iconType: "card",
        label: "Dashboard",
        isAllowed: true,
      },
      {
        id: "shows",
        to: "shows",
        iconType: "screen",
        label: "Shows",
        isAllowed: isCloudUser,
      },
      {
        id: "library",
        to: "shows/library",
        iconType: "library",
        label: "Library",
        isAllowed: isCloudUser,
      },
    ].filter((l) => l.isAllowed),

    adminLinks: [
      {
        id: "account",
        to: "/admin/account",
        iconType: "user",
        label: "Account",
        isAllowed: isLocalUser,
      },
      {
        id: "users",
        to: "/admin/users",
        iconType: "users",
        label: "Users",
        isAllowed: isLocalUser,
      },
      {
        id: "companies",
        to: "/admin/companies",
        iconType: "devices",
        label: "Companies",
        isAllowed: isCloudUser,
      },
      {
        id: "studios",
        to: "/admin/studios",
        iconType: "settings",
        label: "Studios",
        isAllowed: isCloudUser,
      },
    ].filter((l) => l.isAllowed),
  };
};

export const NavigationList = ({ isLocalUser }) => {
  const { companies, company } = useSelector((state) => {
    const c = state.companies;
    return {
      companies: c.companies,
      company: c.company,
    };
  }, shallowEqual);

  const isMobile = useMediaQuery("(max-width: 768px)");

  const { mainLinks, adminLinks } = useMemo(
    () => getNavigationLinks({ isLocalUser }),
    [isLocalUser],
  );

  return (
    <>
      {!!companies?.length && (
        <>
          {mainLinks.map(({ id, to, iconType, label }) => (
            <NavLink
              key={id}
              to={to}
              className={({ isActive }) =>
                `${styles.link} ${button.btnNavigation} ${
                  isActive ? styles.linkactive : ""
                }`
              }
              end
            >
              <Icons
                iconType={iconType}
                fill="var(--color-light)"
                stroke="var(--color-light)"
              />
              {isMobile && label}
            </NavLink>
          ))}
          {!isLocalUser && (
            <>
              <CompanyNavigation companies={companies} company={company} />
              <StudioNavigation />
            </>
          )}
        </>
      )}
      <UserInvitations />
      <UserNavigation adminLinks={adminLinks} isLocalUser={isLocalUser} />
    </>
  );
};
